/**
 * Money formatting — the pure logic behind {@link MoneyText}, split into its
 * own module so `MoneyText.tsx` exports only the component (React Fast
 * Refresh needs a component-only module to hot-reload correctly; mixing in
 * plain function exports trips `react-refresh/only-export-components`).
 *
 * Shell: any.
 */

/** A money figure's semantic role, mapped to `tokens/vendor-app.css`. */
export type MoneyRole = 'in' | 'out' | 'down' | 'flat';

const MINUS = '−';

/** `cents > 0 → 'in'`, `cents < 0 → 'out'`, `cents === 0 → 'flat'`. */
export function roleForCents(cents: number): MoneyRole {
  if (cents > 0) return 'in';
  if (cents < 0) return 'out';
  return 'flat';
}

/** Formats integer cents as `$X.XX`, always two decimal places. */
export function formatMoney(cents: number, { signed = false }: { signed?: boolean } = {}): string {
  const s =
    '$' +
    (Math.abs(cents) / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  if (cents < 0) return MINUS + s;
  return signed && cents > 0 ? '+' + s : s;
}

/** {@link MoneyRole} → the CSS variable it renders with. */
export const MONEY_ROLE_VAR: Record<MoneyRole, string> = {
  in: 'var(--money-in)',
  out: 'var(--money-out)',
  down: 'var(--money-down)',
  flat: 'var(--money-flat)',
};
